/* eslint-disable no-undef */
import { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
  INVALID_PAYMENT_FIELD,
  REQUIRED_PAYMENT_FIELD,
} from '../../constants/localizations/payment';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';

/**
 * A component that is responsible for interpreting the recurly validation messages that
 * are emitted from the library. Each RecurlyField is wrapped with this decorator so
 * that validation handling can be specific to the given RecurlyField.
 *
 *  Also, all Recurly fields are required, except for CVV per documentation:
 *  https://dev.recurly.com/docs/recurly-js-getting-started#section-card-fields
 *
 * Recurly validation model structure:
 * {
 * "fields": {
 *    "month": {
 *      "valid": false,
 *      "empty": true,
 *      "length": 0,
 *      "focus": false
 *    },
 *    "cvv": {
 *      "valid": false,
 *      "empty": true,
 *      "length": 0,
 *      "focus": false
 *    },
 *    "year": {
 *      "valid": false,
 *      "empty": true,
 *      "length": 0,
 *      "focus": false
 *    },
 *    "number": {
 *      "empty": true,
 *      "length": 0,
 *      "focus": false,
 *      "valid": false,
 *      "firstSix": "",
 *      "lastFour": "",
 *      "brand": "unknown"
 *    }
 *  }
 *}
 *
 * @param RecurlyFieldComponent {ReactComponent}
 * @returns RecurlyFieldValidation {ReactComponent}
 */
export default function withRecurlyFieldValidation(RecurlyFieldComponent) {
  class RecurlyFieldValidation extends Component {
    static propTypes = {
      recurlyValidation: PropTypes.object.isRequired,
      customError: PropTypes.string,
      required: PropTypes.bool,
    };

    static contextType = LocationAndLocalizationContext;

    state = {
      isValid: true,
      hasBeenFocused: false,
      errorMessage: null,
    };

    componentDidUpdate(prevProps, prevState) {
      const validation = prevState.isValid !== this.state.isValid;
      const hasBeenFocused =
        get(this.props, 'recurlyValidation.focus') !==
        get(prevProps, 'recurlyValidation.focus');
      if (validation || hasBeenFocused) {
        this.updateValidation(this.props.recurlyValidation);
      }
    }

    updateValidation(fieldValidation) {
      const { hasBeenFocused } = this.state;
      const newState = {};

      if (fieldValidation.focus && !hasBeenFocused) {
        newState.hasBeenFocused = true;
      }

      if (!fieldValidation.focus && hasBeenFocused) {
        const error = this.validateField(fieldValidation);
        newState.isValid = !error;
        newState.errorMessage = error;
      }

      if (fieldValidation.valid) {
        newState.isValid = true;
        newState.errorMessage = null;
      }

      this.setState(newState);
    }

    validateField(fieldValidation) {
      const { getLocalizedText } = this.context;
      const isEmptyAndRequired = fieldValidation.empty && this.props.required;
      const isNotValidAndNotEmpty =
        !fieldValidation.valid && !fieldValidation.empty;

      if (isEmptyAndRequired) {
        return getLocalizedText(REQUIRED_PAYMENT_FIELD);
      } else if (isNotValidAndNotEmpty) {
        return (
          this.props.customError || getLocalizedText(INVALID_PAYMENT_FIELD)
        );
      }

      return null;
    }

    render() {
      return <RecurlyFieldComponent {...this.props} {...this.state} />;
    }
  }
  return RecurlyFieldValidation;
}
